import React, { useContext } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { LeedflowLogoLight } from '@leedpulse-monorepo/assets';
import './SignUpForm.css';
import { Alert, Button, Flex, message } from 'antd';
import { StripeProvider } from '../../contexts/stripe/StripeContext';
import SignupFormContext, { StepIndex } from '../../contexts/forms/SignupFormContext';
import { EmailRecipients } from '@leedpulse-monorepo/enums';

interface SignUpFormProps {
  onBackToLogin: () => void;
}

const LEEDFLOW_BOOKING_URL = import.meta.env.VITE_CALENDLY_ANTHONY_URL;

const SignUpForm: React.FC<SignUpFormProps> = ({ onBackToLogin }) => {
  const {
    data,
    actions,
  } = useContext(SignupFormContext);
  const { steps, currentStep, canProceed, submitButtonTarget, accountCreated, paymentSuccessful, isLoading, isValidActivationCode } = data;
  const { setCurrentStep, handleCreateCompanyAndUser } = actions;

  const handleNext = async () => {
    if (currentStep < steps.length && canProceed) {
      if (currentStep === StepIndex.PLAN) {
        try {
          await handleCreateCompanyAndUser();
          setCurrentStep((prev) => prev + 1);
        } catch (error) {
          console.error('Error creating company and user', error);
          message.error(error instanceof Error ? error.message : `Une erreur est survenue, veuillez contacter un administrateur ${EmailRecipients.JOEY}`);
        }
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  return (
    <StripeProvider>
      <div className="signup">
        {/* Decorative arcs - Top Right */}
        <div className="signup__arc signup__arc--tr-1" />
        <div className="signup__arc signup__arc--tr-2" />
        <div className="signup__arc signup__arc--tr-3" />
        <div className="signup__arc signup__arc--tr-4" />

        {/* Decorative arcs - Bottom Left */}
        <div className="signup__arc signup__arc--bl-1" />
        <div className="signup__arc signup__arc--bl-2" />
        <div className="signup__arc signup__arc--bl-3" />
        <div className="signup__arc signup__arc--bl-4" />

        <div className="signup__container">
          <div className="signup__header">
            <button type="button" className="signup__back-btn" onClick={onBackToLogin}>
              <ArrowLeftOutlined />
            </button>
            <div className="signup__logo">
              <img src={LeedflowLogoLight} alt="LeedFlow" />
            </div>
            <div className="signup__back-btn-placeholder" />
          </div>

          {/* Stepper - Minimal dots (I didn't used the Steps component from antd5 because it hardly sucks) */}
          <div className="signup__stepper">
            {Array.from({ length: steps.length }, (_, i) => i).map((step, index) => (
              <React.Fragment key={step}>
                <div
                  className={`signup__dot ${currentStep === step
                    ? 'signup__dot--active'
                    : currentStep > step
                      ? 'signup__dot--completed'
                      : ''
                    }`}
                />
                {index < steps.length - 1 && (
                  <div
                    className={`signup__dot-connector ${currentStep > step ? 'signup__dot-connector--completed' : ''
                      }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step indicator */}
          <p className="signup__step-indicator-text">
            Étape {currentStep + 1} sur {steps.length}
          </p>

          {/* Step Content */}
          <form
            className="signup__form"
            onSubmit={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canProceed && !accountCreated) {
                e.preventDefault();
                if (currentStep < steps.length - 1) {
                  handleNext();
                } else {
                  // handleSubmitAccountCreation(); // TODO : @MrWarzo gerer ce comportement
                }
              }
            }}
          >
            {steps[currentStep]?.content()}

            {/* Navigation Buttons */}
            {(!accountCreated || !paymentSuccessful) && (
              <div className="signup__navigation">
                {currentStep > 0 && (
                  <Button
                    onClick={handlePrevious}
                  >
                    Précédent
                  </Button>
                )}
                {currentStep < steps.length - 1 ? (
                  <Button
                    type="primary"
                    onClick={handleNext}
                    loading={isLoading}
                    disabled={!canProceed}
                  >
                    Suivant
                  </Button>
                ) : (
                  <div ref={submitButtonTarget} />
                )}
              </div>
            )}
            {accountCreated && paymentSuccessful && (
              <div className="signup__navigation">
                <Button type="primary" onClick={() => window.open(LEEDFLOW_BOOKING_URL, '_blank')}>
                  Prendre rendez-vous
                </Button>
              </div>
            )}
          </form>

          <footer className="signup__footer">
            <p className="signup__copyright">Leedflow • Création d'un compte</p>
          </footer>
        </div>
      </div>
    </StripeProvider>
  );
};

export default SignUpForm;
